/* eslint-disable indent */
/* eslint-disable no-shadow */
import './style.scss';

import { getAll as getAllProjectProposalsDefense } from 'apis/projectProposalsDefenses.api';
import {
  COUNCIL_ROLES_CODE,
  ROLES_ACCESS_COUNCIL_DATA_IN_REVIEW,
  COUNCIL_ROW_LIMIT,
  COUNCIL_TYPE_CODE,
  TIMEZONE_VN,
} from 'constant';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { Link, useLocation, useSearchParams } from 'react-router-dom';

import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { InputText } from 'primereact/inputtext';
import { MultiSelect } from 'primereact/multiselect';
import { Tag } from 'primereact/tag';

import { getAllCouncilTypes } from 'apis/councils.api';
import Pagination from 'components/Pagination';
import {
  getOneReviewProjectProposalCouncilMembers,
  isMemberAllowedReview,
  mapError,
} from 'utils/func';
import SearchFilters from 'features/ProjectProposalDefense/components/SearchFilters';

function ProjectProposalDefenseViewList() {
  // #region Data
  const { t } = useTranslation();
  const location = useLocation();
  const [searchParams] = useSearchParams({ replace: true });
  const [isVisibleSearchFilters, setIsVisibleSearchFilters] = useState(false);

  // Pagination state
  const [pagination, setPagination] = useState({
    first: parseInt(searchParams.get('first'), 10) || 0,
    rowsPerPage: COUNCIL_ROW_LIMIT,
  });

  // Filter states
  const [filtersTable, setFiltersTable] = useState({
    _vietnameseName: { value: null },
    _ownerName: { value: null },
    _councilType: { value: null },
    _reviewStatus: { value: null },
  });

  // Background filters, sync with localStorage
  const [filters, setFilters] = useState({
    vietnameseName: '',
    ownerName: '',
    councilTypes: [],
    reviewStatus: [],
  });

  // Applied filters, change every time click on Apply and Clear button for update
  const [appliedFilters, setAppliedFilters] = useState({
    vietnameseName: '',
    ownerName: '',
    councilTypes: [],
    reviewStatus: [],
  });
  // Initialize filters from localStorage
  useEffect(() => {
    const localFilters = JSON.parse(
      localStorage.getItem(`scientist-${location.pathname.slice(1)}-filters`) ??
        '{ "vietnameseName": "", "ownerName": "", "councilTypes": [] }'
    );
    setFilters(localFilters);
    setAppliedFilters(localFilters);

    // Set rows per page in localStorage
    const localRowsPerPage = parseInt(
      localStorage.getItem(`scientist-${location.pathname.slice(1)}-rowsPerPage`),
      10
    );
    if (localRowsPerPage) {
      setPagination((cur) => ({ ...cur, rowsPerPage: localRowsPerPage }));
    }

    // Set current page (first) with searchParams
    const currentFirst = parseInt(searchParams.get('first'), 10) || 0;
    setPagination((cur) => ({ ...cur, first: currentFirst }));
  }, [location.pathname, searchParams]);

  const { data: councilTypesData } = useQuery(['councilTypes'], getAllCouncilTypes);
  const councilTypes = useMemo(() => {
    if (councilTypesData?.data?.length > 0) {
      return councilTypesData?.data?.map((item) => ({
        code: item?.code,
        name: item?.code ? t(`council.viewList.${item.code}`) : item?.name,
      }));
    }
    return [];
  }, [councilTypesData, t]);

  const reviewStatusOptions = [
    {
      code: 'reviewed',
      name: t('councilStatuses.reviewed'),
    },
    {
      code: 'notReviewed',
      name: t('councilStatuses.notReviewed'),
    },
  ];

  const {
    data: _data,
    isLoading,
    isError,
    error,
  } = useQuery(
    ['projectProposalsDefense', pagination, appliedFilters],
    ({ queryKey: [, _pagination, _filters] }) =>
      getAllProjectProposalsDefense(_pagination, _filters)
  );
  const data = useMemo(
    () => _data?.data ?? { data: [], pagination: { row: COUNCIL_ROW_LIMIT, totalRecords: 0 } },
    [_data]
  );

  // #endregion

  // #region Events
  const handleClick = (value, projectProposalCouncilMembers) => {
    const queryParams = `councilId=${value?.projectProposalCouncils?.[0].council?.id}&councilMemberId=${value?.projectProposalCouncils?.[0].council?.councilMembers[0]?.id}&summaryReportId=${value?.projectProposalCouncils?.[0].council?.summaryReport?.id}&projectId=${value?.projectId}`;
    if (
      value?.projectProposalCouncils?.[0].council?.councilType?.code ===
      COUNCIL_TYPE_CODE.PRELIMINARY_SELECTION_COUNCIL
    ) {
      if (
        new Date() > new Date(value?.projectProposalCouncils?.[0]?.reviewDeadline) ||
        !isMemberAllowedReview(value?.projectProposalCouncils?.[0]?.council?.councilMembers?.[0])
      ) {
        return `/prequalification-review/detail/${value?.id}?${queryParams}`;
      }
      if (projectProposalCouncilMembers?.[0]?.isReviewOptionForm) {
        return `/prequalification-review/edit/${value?.id}?${queryParams}`;
      }
      return `/prequalification-review/create/${value?.id}?${queryParams}`;
    }

    if (
      value?.projectProposalCouncils?.[0].council?.councilType?.code ===
      COUNCIL_TYPE_CODE.SPECIALIZATION_COUNCIL
    ) {
      if (
        new Date() > new Date(value?.projectProposalCouncils?.[0]?.reviewDeadline) ||
        !isMemberAllowedReview(value?.projectProposalCouncils?.[0]?.council?.councilMembers?.[0])
      ) {
        return `/specialize-review/detail/${value?.id}?${queryParams}`;
      }
      if (projectProposalCouncilMembers?.[0]?.isReviewOptionForm) {
        return `/specialize-review/edit/${value?.id}?${queryParams}`;
      }
      return `/specialize-review/create/${value?.id}?${queryParams}`;
    }

    if (
      value?.projectProposalCouncils?.[0].council?.councilType?.code ===
      COUNCIL_TYPE_CODE.FINANCE_COUNCIL
    ) {
      if (
        new Date() > new Date(value?.projectProposalCouncils?.[0]?.reviewDeadline) ||
        !isMemberAllowedReview(value?.projectProposalCouncils?.[0]?.council?.councilMembers?.[0])
      ) {
        return `/financial-review/detail/${value?.id}?${queryParams}`;
      }
      if (projectProposalCouncilMembers?.[0]?.isReviewOptionForm) {
        return `/financial-review/edit/${value?.id}?${queryParams}`;
      }
      return `/financial-review/create/${value?.id}?${queryParams}`;
    }

    if (
      value?.projectProposalCouncils?.[0].council?.councilType?.code ===
      COUNCIL_TYPE_CODE.MIDTERM_REPORT_COUNCIL
    ) {
      if (
        new Date() > new Date(value?.projectProposalCouncils?.[0]?.reviewDeadline) ||
        !isMemberAllowedReview(value?.projectProposalCouncils?.[0]?.council?.councilMembers?.[0])
      ) {
        return `/midterm-review/detail/${value?.id}?${queryParams}`;
      }
      if (projectProposalCouncilMembers?.[0]?.isReviewOptionForm) {
        return `/midterm-review/edit/${value?.id}?${queryParams}`;
      }
      return `/midterm-review/create/${value?.id}?${queryParams}`;
    }

    if (
      value?.projectProposalCouncils?.[0].council?.councilType?.code ===
      COUNCIL_TYPE_CODE.ACCEPTANCE_COUNCIL
    ) {
      if (
        new Date() > new Date(value?.projectProposalCouncils?.[0]?.reviewDeadline) ||
        !isMemberAllowedReview(value?.projectProposalCouncils?.[0]?.council?.councilMembers?.[0])
      ) {
        return `/acceptance-review/detail/${value?.id}?${queryParams}`;
      }
      if (projectProposalCouncilMembers?.[0]?.isReviewOptionForm) {
        return `/acceptance-review/edit/${value?.id}?${queryParams}`;
      }
      return `/acceptance-review/create/${value?.id}?${queryParams}`;
    }

    if (
      value?.projectProposalCouncils?.[0].council?.councilType?.code ===
      COUNCIL_TYPE_CODE.LIQUIDATION_COUNCIL
    ) {
      if (
        new Date() > new Date(value?.projectProposalCouncils?.[0]?.reviewDeadline) ||
        !isMemberAllowedReview(value?.projectProposalCouncils?.[0]?.council?.councilMembers?.[0])
      ) {
        return `/liquidation-review/detail/${value?.id}?${queryParams}`;
      }
      if (projectProposalCouncilMembers?.[0]?.isReviewOptionForm) {
        return `/liquidation-review/edit/${value?.id}?${queryParams}`;
      }
      return `/liquidation-review/create/${value?.id}?${queryParams}`;
    }

    return '';
  };
  // #endregion

  const formatActions = (value) => {
    const projectProposalCouncilMembers = getOneReviewProjectProposalCouncilMembers(
      value?.projectProposalCouncils?.[0].council?.councilMembers?.[0]
        ?.projectProposalCouncilMembers,
      value?.reference
    );
    const isReviewDeadlinePassed =
      new Date() > new Date(value?.projectProposalCouncils?.[0]?.reviewDeadline);

    const ppCouncil = value?.projectProposalCouncils?.[0];

    let secondAccessCond = false;

    if (
      ((ppCouncil?.projectProposal?.shortenedReview &&
        ppCouncil?.council?.councilType?.code === COUNCIL_TYPE_CODE.SPECIALIZATION_COUNCIL) ||
        ppCouncil?.council?.councilType?.code === COUNCIL_TYPE_CODE.FINANCE_COUNCIL) &&
      ppCouncil?.council?.councilMembers?.[0]?.councilMemberRole?.code ===
        COUNCIL_ROLES_CODE.GROUP_LEADER
    ) {
      secondAccessCond = true;
    }

    const isViewCouncilData =
      ROLES_ACCESS_COUNCIL_DATA_IN_REVIEW.includes(
        ppCouncil?.council?.councilMembers?.[0]?.councilMemberRole?.code
      ) || secondAccessCond;

    return (
      <div className="flex gap-2 justify-content-center">
        {isMemberAllowedReview(value?.projectProposalCouncils?.[0]?.council?.councilMembers?.[0]) &&
          (isReviewDeadlinePassed ? (
            <Link
              to={handleClick(value, projectProposalCouncilMembers)}
              state={{
                isViewCouncilData,
              }}
            >
              <Button
                className="bg-green-600 border-green-600 hover:bg-green-700 hover:border-green-700"
                icon="pi pi-comment"
                severity="info"
                tooltip={t('council.view.viewReview')}
                tooltipOptions={{ position: 'left' }}
              />
            </Link>
          ) : (
            <Link
              to={handleClick(value, projectProposalCouncilMembers)}
              state={{
                fromInternal: true,
                isViewCouncilData,
              }}
            >
              <Button
                className="bg-orange-600 border-orange-600 hover:bg-orange-700 hover:border-orange-700"
                icon="pi pi-pencil"
                severity="info"
                tooltip={t('council.review.createOrUpdate')}
                tooltipOptions={{ position: 'left' }}
              />
            </Link>
          ))}
      </div>
    );
  };

  const formatName = (value) => {
    const projectProposalCouncilMembers = getOneReviewProjectProposalCouncilMembers(
      value?.projectProposalCouncils?.[0].council?.councilMembers?.[0]
        ?.projectProposalCouncilMembers,
      value?.reference
    );
    const link = handleClick(value, projectProposalCouncilMembers);

    const ppCouncil = value?.projectProposalCouncils?.[0];

    let secondAccessCond = false;

    if (
      ((ppCouncil?.projectProposal?.shortenedReview &&
        ppCouncil?.council?.councilType?.code === COUNCIL_TYPE_CODE.SPECIALIZATION_COUNCIL) ||
        ppCouncil?.council?.councilType?.code === COUNCIL_TYPE_CODE.FINANCE_COUNCIL) &&
      ppCouncil?.council?.councilMembers?.[0]?.councilMemberRole?.code ===
        COUNCIL_ROLES_CODE.GROUP_LEADER
    ) {
      secondAccessCond = true;
    }

    const isViewCouncilData =
      ROLES_ACCESS_COUNCIL_DATA_IN_REVIEW.includes(
        ppCouncil?.council?.councilMembers?.[0]?.councilMemberRole?.code
      ) || secondAccessCond;

    return (
      <Link
        to={link}
        state={{
          fromInternal: true,
          isViewCouncilData,
        }}
        className="hover:underline"
      >
        {value?.vietnameseName}
      </Link>
    );
  };

  const formatRole = (value) =>
    value?.projectProposalCouncils?.[0]?.council?.councilMembers?.[0]?.councilMemberRole?.code &&
    t(
      `council.role.${value?.projectProposalCouncils?.[0]?.council?.councilMembers?.[0]?.councilMemberRole?.code}`
    );

  const formatReviewDeadline = (value) => {
    if (value?.projectProposalCouncils?.[0]?.reviewDeadline < new Date().toISOString()) {
      return (
        <span className="text-red-600">
          {value?.projectProposalCouncils?.[0]?.reviewDeadline
            ? new Date(value?.projectProposalCouncils?.[0]?.reviewDeadline).toLocaleDateString(
                'vi',
                { timeZone: TIMEZONE_VN }
              )
            : ''}
          <div>{t('projectProposalCouncil.viewList.expiredReviewDeadline')}</div>
        </span>
      );
    }
    return value?.projectProposalCouncils?.[0]?.reviewDeadline
      ? new Date(value?.projectProposalCouncils?.[0]?.reviewDeadline).toLocaleDateString('vi', {
          timeZone: TIMEZONE_VN,
        })
      : '';
  };

  const formatTimeMet = (council) =>
    council?.projectProposalCouncils?.[0]?.timeMet
      ? new Date(council?.projectProposalCouncils?.[0]?.timeMet).toLocaleDateString('vi', {
          timeZone: TIMEZONE_VN,
        })
      : '';

  const formatOwnerName = (value) => value?.projectProposalContacts?.[0]?.fullname;

  const formatCouncilType = (value) => {
    const isShortenedReview = value?.projectProposalCouncils?.[0]?.projectProposal?.shortenedReview;
    const councilTypeCode = value?.projectProposalCouncils?.[0]?.council?.councilType?.code;
    if (isShortenedReview && councilTypeCode === COUNCIL_TYPE_CODE.SPECIALIZATION_COUNCIL) {
      return t(
        `council.view.councilType.${value?.projectProposalCouncils?.[0]?.council?.councilType?.code}AndFinance`
      );
    }

    return t(
      `council.view.councilType.${value?.projectProposalCouncils?.[0]?.council?.councilType?.code}`
    );
  };

  const formatReviewStatus = (value) => {
    const statusColorMapping = {
      reviewed: 'success',
      notReviewed: 'info',
    };

    const { reviewStatus } = value;
    if (!reviewStatus) return '';

    return (
      <Tag
        style={{ maxWidth: '8rem' }}
        severity={statusColorMapping[reviewStatus]}
        value={t(`councilStatuses.${reviewStatus}`)}
        rounded
      />
    );
  };

  // Filter functions
  const vietnameseNameFilter = () => (
    <InputText
      value={filters.vietnameseName}
      onChange={(e) => {
        const newFilters = { ...filters, vietnameseName: e.target.value };
        setFilters(newFilters);
        localStorage.setItem(
          `scientist-${location.pathname.slice(1)}-filters`,
          JSON.stringify(newFilters)
        );
      }}
      placeholder={t('projectProposal.viewList.searchByName')}
      className="p-column-filter"
    />
  );

  const ownerNameFilter = () => (
    <InputText
      value={filters.ownerName}
      onChange={(e) => {
        const newFilters = { ...filters, ownerName: e.target.value };
        setFilters(newFilters);
        localStorage.setItem(
          `scientist-${location.pathname.slice(1)}-filters`,
          JSON.stringify(newFilters)
        );
      }}
      placeholder={t('projectProposal.viewList.searchByOwner')}
      className="p-column-filter"
    />
  );

  const councilTypeFilter = () => (
    <MultiSelect
      value={filters.councilTypes}
      onChange={(e) => {
        const newFilters = { ...filters, councilTypes: e.value };
        setFilters(newFilters);
        localStorage.setItem(
          `scientist-${location.pathname.slice(1)}-filters`,
          JSON.stringify(newFilters)
        );
      }}
      options={councilTypes}
      optionLabel="name"
      optionValue="code"
      filter
      showClear
      placeholder={t('projectProposal.viewList.councilType')}
      maxSelectedLabels={3}
      style={{ maxWidth: '22rem', width: '18.5rem' }}
    />
  );

  const reviewStatusFilter = () => (
    <MultiSelect
      value={filters.reviewStatus}
      onChange={(e) => {
        const newFilters = { ...filters, reviewStatus: e.value };
        setFilters(newFilters);
        localStorage.setItem(
          `scientist-${location.pathname.slice(1)}-filters`,
          JSON.stringify(newFilters)
        );
      }}
      options={reviewStatusOptions}
      optionLabel="name"
      optionValue="code"
      filter
      showClear
      placeholder={t('projectProposal.viewList.status')}
      maxSelectedLabels={3}
      style={{ maxWidth: '22rem', width: '18.5rem' }}
    />
  );

  const filterClearTemplate = (options) => (
    <Button
      type="button"
      label={t('employee.viewList.clear')}
      onClick={options.filterClearCallback}
      severity="secondary"
      outlined
    />
  );

  const filterApplyTemplate = (options) => (
    <Button
      type="button"
      label={t('employee.viewList.apply')}
      onClick={options.filterApplyCallback}
      severity="success"
    />
  );

  const filterActiveStyle = (field) =>
    filters[field] && filters[field].length > 0 ? { color: '#22C55E' } : { color: '#6c757d' };
  // #endregion

  const renderHeader = () => (
    <div className="flex justify-content-between">
      <Button
        type="button"
        icon="pi pi-filter"
        label={t('projectProposalCouncil.viewList.searchFilters')}
        outlined
        onClick={() => setIsVisibleSearchFilters(true)}
      />
    </div>
  );

  return (
    <div className="grid">
      <div className="col-12">
        <div className="card">
          {isError && <h3 className="text-red-600">{mapError(t, error)}</h3>}
          <div className="flex justify-content-between align-items-end mb-4 pb-2">
            <h5 className="p-0 m-0">{t('projectProposalCouncil.viewList.title')}</h5>
          </div>

          <DataTable
            value={data?.data ?? []}
            className="p-datatable-gridlines header-table"
            showGridlines
            dataKey="key"
            filters={filtersTable}
            filterDisplay="menu"
            onFilter={(e) => setFiltersTable(e.filters)}
            loading={isLoading}
            emptyMessage={t('projectProposal.viewList.emptyMessage')}
            header={renderHeader}
          >
            <Column
              field="key"
              header={t('council.viewList.order')}
              headerStyle={{ justifyContent: 'center' }}
              style={{ minWidth: '2rem', textAlign: 'center' }}
            />
            <Column
              header={t('projectProposal.viewList.nameVN')}
              style={{ minWidth: '12rem' }}
              body={formatName}
              filter
              filterField="_vietnameseName"
              filterElement={vietnameseNameFilter}
              filterClear={filterClearTemplate}
              filterApply={filterApplyTemplate}
              showFilterMenu
              showFilterMatchModes={false}
              onFilterClear={() => {
                const newFilters = { ...filters, vietnameseName: '' };
                setFilters(newFilters);
                setAppliedFilters(newFilters);
                localStorage.setItem(
                  `scientist-${location.pathname.slice(1)}-filters`,
                  JSON.stringify(newFilters)
                );
                setPagination((prev) => ({ ...prev, first: 0 }));
              }}
              onFilterApplyClick={() => {
                setAppliedFilters(filters);
                setPagination((prev) => ({ ...prev, first: 0 }));
              }}
              headerStyle={filterActiveStyle('vietnameseName')}
            />
            <Column
              header={t('projectProposal.viewList.owner')}
              style={{ minWidth: '8rem', textAlign: 'center' }}
              body={formatOwnerName}
              filter
              filterField="_ownerName"
              filterElement={ownerNameFilter}
              filterClear={filterClearTemplate}
              filterApply={filterApplyTemplate}
              showFilterMenu
              showFilterMatchModes={false}
              onFilterClear={() => {
                const newFilters = { ...filters, ownerName: '' };
                setFilters(newFilters);
                setAppliedFilters(newFilters);
                localStorage.setItem(
                  `scientist-${location.pathname.slice(1)}-filters`,
                  JSON.stringify(newFilters)
                );
                setPagination((prev) => ({ ...prev, first: 0 }));
              }}
              onFilterApplyClick={() => {
                setAppliedFilters(filters);
                setPagination((prev) => ({ ...prev, first: 0 }));
              }}
              headerStyle={filterActiveStyle('ownerName')}
            />
            <Column
              header={t('council.fields.role')}
              headerStyle={{ justifyContent: 'center' }}
              style={{ minWidth: '8rem', textAlign: 'center' }}
              body={formatRole}
            />
            <Column
              header={t('council.viewList.councilType')}
              style={{ minWidth: '8rem', textAlign: 'center' }}
              body={formatCouncilType}
              filter
              filterField="_councilType"
              filterElement={councilTypeFilter}
              filterClear={filterClearTemplate}
              filterApply={filterApplyTemplate}
              showFilterMenu
              showFilterMatchModes={false}
              onFilterClear={() => {
                const newFilters = { ...filters, councilTypes: [] };
                setFilters(newFilters);
                setAppliedFilters(newFilters);
                localStorage.setItem(
                  `scientist-${location.pathname.slice(1)}-filters`,
                  JSON.stringify(newFilters)
                );
                setPagination((prev) => ({ ...prev, first: 0 }));
              }}
              onFilterApplyClick={() => {
                setAppliedFilters(filters);
                setPagination((prev) => ({ ...prev, first: 0 }));
              }}
              headerStyle={filterActiveStyle('councilTypes')}
            />
            <Column
              header={t('projectProposal.viewList.reviewDeadline')}
              dataType="numeric"
              style={{ minWidth: '8rem', textAlign: 'center' }}
              body={formatReviewDeadline}
            />
            <Column
              header={t('council.fields.meetingDate')}
              style={{ minWidth: '7rem', textAlign: 'center' }}
              body={formatTimeMet}
            />
            <Column
              header={t('council.fields.status')}
              style={{ minWidth: '8rem', textAlign: 'center' }}
              field="reviewStatus"
              body={formatReviewStatus}
              filter
              filterField="_reviewStatus"
              filterElement={reviewStatusFilter}
              filterClear={filterClearTemplate}
              filterApply={filterApplyTemplate}
              showFilterMenu
              showFilterMatchModes={false}
              onFilterClear={() => {
                const newFilters = { ...filters, reviewStatus: [] };
                setFilters(newFilters);
                setAppliedFilters(newFilters);
                localStorage.setItem(
                  `scientist-${location.pathname.slice(1)}-filters`,
                  JSON.stringify(newFilters)
                );
                setPagination((prev) => ({ ...prev, first: 0 }));
              }}
              onFilterApplyClick={() => {
                setAppliedFilters(filters);
                setPagination((prev) => ({ ...prev, first: 0 }));
              }}
              headerStyle={filterActiveStyle('reviewStatus')}
            />
            <Column
              header={t('projectProposal.action')}
              body={formatActions}
              style={{ width: '6.5rem', textAlign: 'center' }}
              frozen
              alignFrozen="right"
            />
          </DataTable>

          <Pagination
            pagination={pagination}
            setPagination={setPagination}
            paginationData={data.pagination}
            pageName="scientist-project-proposal-defense"
          />
        </div>
      </div>

      {isVisibleSearchFilters && (
        <SearchFilters
          visible={isVisibleSearchFilters}
          setVisible={setIsVisibleSearchFilters}
          filters={filters}
          setFilters={setFilters}
          appliedFilters={appliedFilters}
          setAppliedFilters={setAppliedFilters}
          councilTypes={councilTypes}
          reviewStatusOptions={reviewStatusOptions}
        />
      )}
    </div>
  );
}

export default ProjectProposalDefenseViewList;
